import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import CustomInput from "../../common/customInput/customInput";
import CustomButton from "../../common/customButton/customButton";
import logo from "../../assets/ksolactipanel.png";
import gcseLogo from "../../assets/KSOL GCSE_logo.png";
import BackButton from "../../common/backButton/backButton";
import "./loginScreen.css";
import useCustomInput from "../../common/customInput/hook/useCustomInput";
import { useSharedState } from "../../context/formsContext";
import UniformLoader from "../../common/uniformLoader/uniformLoader";
import { useNavigate } from "react-router-dom";
import { responsiveFontSizes } from "@mui/material";
import { getProperty, getNestedProperty } from "../../utils/apiResponseHelper";

const LoginScreen = () => {
  const { handleInputChange } = useCustomInput();
  const {
    value,
    setValue,
    setLoginToken,
    setLoading,
    loading,
    setMentorId,
    setMentorEmail,
    prefix,
  } = useSharedState();

  const [loginErrorMessage, setLoginErrorMessage] = useState("");
  const [emailErrorMessage, setEmailErrorMessage] = useState("");
  const [usernameLoginHint, setUsernameLoginHint] = useState("");

  const [sessionCourseId, setSessionCourseId] = useState(null);

  // Removed setUrlPrefix - not used in API calls and was causing confusion
  // API calls use 'prefix' from context which is set to process.env.REACT_APP_BASE_URL || "https://ksol.co.uk"

  const navigate = useNavigate();

  useEffect(() => {
    const courseId = localStorage.getItem("gcse");
    if (courseId === "true") {
      setSessionCourseId(true);
    } else {
      setSessionCourseId(false);
    }
  }, []);

  // Check for email in sessionStorage from free downloads registration
  useEffect(() => {
    const loginRequiresUsername =
      sessionStorage.getItem('loginRequiresUsername') === 'true';

    if (loginRequiresUsername) {
      sessionStorage.removeItem('loginRequiresUsername');
      sessionStorage.removeItem('pendingLoginEmail');
      setUsernameLoginHint("Please use your KSOL username and password to log in.");
      setValue((prevValue) => ({
        ...prevValue,
        email: "",
      }));
      return;
    }

    const pendingEmail = sessionStorage.getItem('pendingLoginEmail');
    if (pendingEmail && !value.email) {
      setUsernameLoginHint("");
      setValue((prevValue) => ({
        ...prevValue,
        email: pendingEmail,
      }));
      // Store email in localStorage for persistence (for full account signup later)
      localStorage.setItem('email', pendingEmail);
      // Clear the sessionStorage after using it
      sessionStorage.removeItem('pendingLoginEmail');
    }
  }, [setValue, value.email]);

  useEffect(() => {
    localStorage.removeItem("initalLoadComplete");
    localStorage.removeItem("isGcse");
    localStorage.removeItem("loginToken");
    localStorage.removeItem("mentorEmail");
    localStorage.removeItem("mentorId");
    localStorage.removeItem("previousPage");
    localStorage.removeItem("studentId");
    localStorage.removeItem("testData");
  }, []);

  const verifyEmail = async () => {
    try {
      const response = await fetch(
        `${prefix}/api40/Registration/GetVerifiedEmail?Email=${value.email}`,
        {
          headers: {
            'X-API-Key': '8dnd6%3258%&k+_FsaTK25SCBNE58*t9'
          }
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch");
      }
      const data = await response.json();

      if (data.length === 0) {
        setEmailErrorMessage("Invalid Email");
      } else {
        setEmailErrorMessage("");
      }

      // Check both uppercase and lowercase property names for compatibility
      const verified = getProperty(data[0] || data, 'verified') || data[0]?.Verified || data[0]?.verified || data?.Verified || data?.verified;
      if (verified === "Y") {
        return true;
      } else {
        navigate("/verification");
        return false;
      }
    } catch (error) {
      return false;
    }
  };

  const validateEmailAndPassword = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isEmail = emailRegex.test(value.email.trim());
    const popupWindow = window.open("about:blank", "_blank");
    setLoading(true);

      try {
        const encodedUsername = encodeURIComponent(value.email || "");
        const encodedPassword = encodeURIComponent(value.password || "");
        const response = await fetch(
          `${prefix}/api40/Account/FreeDownloadMentorLogin?Username=${encodedUsername}&Password=${encodedPassword}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-API-Key": "8dnd6%3258%&k+_FsaTK25SCBNE58*t9"
            },
          }
        );

        if (!response.ok) {
          setLoginErrorMessage("Invalid email, username or password");
          popupWindow?.close();
          throw new Error("Failed to fetch");
        } else {
          setLoginErrorMessage("");
        }

        const data = await response.json();

        if (data.length === 0) {
          setLoginErrorMessage("Invalid email, username or password");
          popupWindow?.close();
          throw new Error("Failed to fetch");
        } else {
          setLoginErrorMessage("");
        }

        // Get properties with fallback for case sensitivity
        const token = getProperty(data, 'token') || data.Token || data.token;
        if (token) {
          setLoginToken(token);
          localStorage.setItem("loginToken", token);
        }

        const mentorId = getNestedProperty(data, 'studentUserDTO.MentorId') || 
                        getNestedProperty(data, 'studentUserDTO.mentorId') ||
                        data.studentUserDTO?.MentorId || data.studentUserDTO?.mentorId;
        if (mentorId) {
          setMentorId(mentorId);
          localStorage.setItem("mentorId", mentorId);
        }

        const mentorEmail = getNestedProperty(data, 'studentUserDTO.MentorEmail') || 
                           getNestedProperty(data, 'studentUserDTO.mentorEmail') ||
                           data.studentUserDTO?.MentorEmail || data.studentUserDTO?.mentorEmail;
        if (mentorEmail) {
          setMentorEmail(mentorEmail);
          localStorage.setItem("mentorEmail", mentorEmail);
        }

        // Store the login email in localStorage for full account signup later
        // This ensures the email persists even after login
        if (value.email && value.email.trim()) {
          localStorage.setItem("email", value.email.trim());
        }

        const isAuthSuccessful = getProperty(data, 'isAuthSuccessful') || data.IsAuthSuccessful || data.isAuthSuccessful;
        if (isAuthSuccessful && popupWindow) {
          popupWindow.location.href = `/FreeDownloads?redirect=dashboard&ts=${Date.now()}`;
        } else {
          popupWindow?.close();
        }
      } catch (error) {
        popupWindow?.close();
      }
    

    setLoading(false);
  };

  if (loading) {
    return (
      <UniformLoader
        title="Loading..."
        message="Please wait while we load your account"
        subMessage=""
      />
    );
  }

  return (
    <>
      <Helmet>
        <title>Login | KSOL</title>
        <meta name="description" content="Login to your KSOL account to access 11+ practice papers, benchmark tests, and personalized learning resources." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="w-full min-h-screen flex flex-col items-center justify-start bg-image p-4">
      <div className="w-full max-w-[80%] flex flex-col items-center p-6 xl:p-8">
        <div className="flex flex-col items-center w-full justify-center ">
          <div className="w-full p-4 flex justify-start items-start">
            <BackButton onClick={() => navigate("/")} />
          </div>
          <img
            className={
              sessionCourseId ? "w-4/12 mb-4 " : "w-1/2  lg:w-3/12 mb-4 "
            }
            src={sessionCourseId ? gcseLogo : logo}
          />
          <div className="w-3/5  h-auto orange-background-registered w-full flex justify-center drop-shadow-lg md:w-3/5">
            <div className="w-4/5 flex  flex-col items-center">
              <h2 className="text-white mt-4">Log In </h2>
              {usernameLoginHint && (
                <p className="text-white text-sm text-center mt-3 mb-2">
                  {usernameLoginHint}
                </p>
              )}

              <div className="w-full lg:w-4/5  mt-10 text-left justify-center">
                <h3 className="text-white">Username</h3>
                <CustomInput
                  onChange={(event) => handleInputChange(event, "email")}
                  value={value.email}
                />
                <h3 className="text-white">Password</h3>
                <CustomInput
                  inputType="password"
                  type="password"
                  onChange={(event) => handleInputChange(event, "password")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      validateEmailAndPassword();
                    }
                  }}
                  value={value.password}
                />
                {loginErrorMessage && (
                  <div className="flex justify-center mb-4">
                    <div className="bg-white inline-block rounded-xl">
                      <p className="text-red-500 text-sm px-4 py-2">
                        {loginErrorMessage}
                      </p>
                    </div>
                  </div>
                )}
                {emailErrorMessage && (
                  <div className="flex justify-center mb-4">
                    <div className="bg-white inline-block rounded-xl">
                      <p className="text-red-500 text-sm px-4 py-2">
                        {emailErrorMessage}
                      </p>
                    </div>
                  </div>
                )}

                <div className="mb-10">
                  <p
                    onClick={() =>
                      window.open(
                        "https://www.ksol.co.uk/library/ksol_send_username.aspx"
                      )
                    }
                    className="text-white mb-4 underline text-center cursor-pointer"
                  >
                    Forgotten Password?
                  </p>
                  <div className="w-full flex justify-center">
                    <CustomButton
                      onClick={validateEmailAndPassword}
                      text={"Log In"}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default LoginScreen;
